import type {
  FhbOutput,
  FhbRuleSet,
  FhbScheme,
  FhbState,
  SchemeVerdict,
} from "./types.js";

// Plain-English first-home-scheme result copy. Lives in the rules package so
// every surface renders identical wording from one source — a regulatory
// requirement, not just DRY.
//
// Posture (binding — panel regulatory HIGH, C1 family): this is INFORMATION, not
// advice. We STATE which schemes a buyer appears eligible for and how they were
// gated, with the SOURCE and a mandatory "as at <date>, verify with <office>"
// caveat on every amount. NO "you'll get $X", NO "you should use FHSS", NO
// "you'll save". Amounts are scheme MAXIMUMS with their gates. The QLD FHOG
// sunset (30 Jun 2026) and per-state reprice schedules make currency caveats
// load-bearing: a stale "$30k" or "you're exempt" is an ACL s18 misleading
// representation of an entitlement.

const SCHEME_LABEL: Record<FhbScheme, string> = {
  five_percent_deposit: "Australian Government 5% Deposit Scheme",
  fhss: "First Home Super Saver scheme",
  fhog: "First Home Owner Grant",
  stamp_duty_relief: "First-home stamp-duty exemption / concession",
};

export function schemeLabel(scheme: FhbScheme): string {
  return SCHEME_LABEL[scheme];
}

const STATE_REVENUE_OFFICE: Record<FhbState, { name: string; url: string }> = {
  NSW: { name: "Revenue NSW", url: "https://www.revenue.nsw.gov.au" },
  VIC: { name: "the State Revenue Office Victoria", url: "https://www.sro.vic.gov.au" },
  QLD: { name: "the Queensland Revenue Office", url: "https://qro.qld.gov.au" },
};

/** The mandatory disclaimer rendered on every first-home-scheme surface. */
export const FHB_DISCLAIMER =
  "Educational tool. Not financial advice. This is general information about " +
  "government schemes, not a recommendation about any home loan, lender or product. " +
  "Eligibility and amounts change — verify with the scheme and your state revenue office " +
  "before you act.";

/** Headline: which schemes apply, framed as eligibility (never a saving). */
export function eligibilityHeadline(output: FhbOutput): string {
  if (output.eligibleSchemes.length === 0) {
    return "On these figures, you do not appear eligible for any of the four schemes — which still saves wasted applications.";
  }
  const names = output.eligibleSchemes.map(schemeLabel).join(", ");
  return `On these figures you appear eligible for: ${names}.`;
}

/** One bullet per scheme verdict — states the gates, no advice. */
export function verdictBullet(verdict: SchemeVerdict): string {
  const head = verdict.eligible
    ? `${schemeLabel(verdict.scheme)} — appears available`
    : `${schemeLabel(verdict.scheme)} — does not appear available`;
  return `${head}: ${verdict.reasons.join(" ")}`;
}

/**
 * The mandatory "as at <date>, verify with <state revenue office>" caveat for a
 * jurisdiction's amounts. Required on every amount per the regulatory posture.
 */
export function currencyCaveat(state: FhbState, rules: FhbRuleSet): string {
  const office = STATE_REVENUE_OFFICE[state];
  return (
    `These ${state} amounts and thresholds are as at ${rules.sourcesVerifiedAt} ` +
    `(${rules.periodLabel}). They reprice on their own schedule — verify with ` +
    `${office.name} (${office.url}) before you act.`
  );
}

export function explanationBullets(output: FhbOutput, state: FhbState, rules: FhbRuleSet): string[] {
  return [
    eligibilityHeadline(output),
    ...output.verdicts.map(verdictBullet),
    ...output.stackingNotes,
    currencyCaveat(state, rules),
    FHB_DISCLAIMER,
  ];
}
